import React, { useState, useEffect, useCallback } from 'react';
import './BookingHistory.css';
import { getBookingRequests, getBookingRequestById } from '../../services/bookingRequestsService';
import { getCurrentUser } from '../../services/authService';

const BookingHistory = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingBooking, setViewingBooking] = useState(null);
  const [bookingHistory, setBookingHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [totalCount, setTotalCount] = useState(0);

  // Map API response to component format
  const mapBookingFromAPI = useCallback((apiRequest) => {
    // Format date from API (ISO format)
    const formatDate = (dateString) => {
      if (!dateString) return '';
      try {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      } catch (e) {
        return dateString;
      }
    };

    // Map status from API to display format
    const mapStatus = (status) => {
      switch (status) {
        case 'Confirmed':
          return 'Accept';
        case 'Rejected':
          return 'Reject';
        case 'Pending':
        case 'Contacted':
          return 'Pending';
        case 'Cancelled':
          return 'Cancelled';
        default:
          return status || 'Pending';
      }
    };

    return {
      id: apiRequest.request_id || apiRequest.id?.toString() || '',
      numericId: apiRequest.id || null,
      bookingDate: formatDate(apiRequest.requested_date || apiRequest.created_at),
      fromLocation: apiRequest.from_location || '',
      toLocation: apiRequest.to_location || '',
      vehicleNumber: apiRequest.vehicle_type || apiRequest.vehicle_number || 'N/A',
      driverName: apiRequest.driver_name || apiRequest.driver?.name || 'N/A',
      phone: apiRequest.driver_phone || apiRequest.driver?.phone || '',
      status: mapStatus(apiRequest.status),
      products: (apiRequest.products || []).map(product => ({
        productName: product.product_name || '',
        productCategory: product.product_category || '',
        customCategory: product.custom_category || '',
        length: product.length_cm?.toString() || '',
        height: product.height_cm?.toString() || '',
        width: product.width_cm?.toString() || '',
        weight: product.weight_kg?.toString() || ''
      }))
    };
  }, []);

  // Fetch booking history from API
  const fetchBookingHistory = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const currentUser = getCurrentUser();
      const customerId = currentUser?.customer_id || currentUser?.id;

      if (!customerId) {
        throw new Error('Customer ID not found. Please login again.');
      }

      const params = {
        page: currentPage,
        limit: itemsPerPage,
      };

      if (searchQuery) {
        params.search = searchQuery;
      }

      // Filter by customer_id - the API should filter by the logged-in customer
      // If the API doesn't support customer_id filter, we'll filter client-side
      const response = await getBookingRequests(params);

      // Extract booking requests from response
      let requestsData = [];
      if (response.data) {
        requestsData = response.data.items || response.data.requests || response.data.data || [];
      } else if (Array.isArray(response)) {
        requestsData = response;
      } else if (response.items || response.requests) {
        requestsData = response.items || response.requests;
      }

      // Filter by customer_id if API doesn't do it
      const customerRequests = requestsData.filter(request => 
        request.customer_id === customerId || 
        request.customer_id?.toString() === customerId?.toString()
      );

      // Map API response to component format
      const mappedBookings = customerRequests.map(request => mapBookingFromAPI(request));

      setBookingHistory(mappedBookings);

      // Get total count from response or use filtered length
      const total = response.data?.total || response.total || customerRequests.length;
      setTotalCount(total);
    } catch (err) {
      console.error('Error fetching booking history:', err);
      setError(err.message || 'Failed to fetch booking history. Please try again.');
      setBookingHistory([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, searchQuery, mapBookingFromAPI]);

  // Fetch booking history on component mount and when dependencies change
  useEffect(() => {
    fetchBookingHistory();
  }, [fetchBookingHistory]);

  // Handle view booking - fetch full details
  const handleViewBooking = async (booking) => {
    try {
      // If we have numericId, fetch full details from API
      if (booking.numericId) {
        setLoading(true);
        const response = await getBookingRequestById(booking.numericId);
        
        // Extract booking request data from response
        let requestData = null;
        if (response.data) {
          requestData = response.data.request || response.data.booking_request || response.data;
        } else if (response.request || response.booking_request) {
          requestData = response.request || response.booking_request;
        } else {
          requestData = response;
        }

        if (requestData) {
          const fullBooking = mapBookingFromAPI(requestData);
          setViewingBooking(fullBooking);
        } else {
          // Fallback to the booking from list
          setViewingBooking(booking);
        }
      } else {
        // Use the booking from list
        setViewingBooking(booking);
      }
      setShowViewModal(true);
    } catch (err) {
      console.error('Error fetching booking details:', err);
      // Fallback to the booking from list
      setViewingBooking(booking);
      setShowViewModal(true);
    } finally {
      setLoading(false);
    }
  };

  // Filter history (client-side filtering for search)
  const filteredHistory = bookingHistory.filter(booking =>
    booking.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
    booking.fromLocation.toLowerCase().includes(searchQuery.toLowerCase()) ||
    booking.toLocation.toLowerCase().includes(searchQuery.toLowerCase()) ||
    booking.vehicleNumber.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Calculate pagination
  const totalPages = Math.ceil(totalCount / itemsPerPage);
  const paginatedHistory = filteredHistory.slice(0, itemsPerPage); // API already paginates, but we filter client-side for search

  const getStatusClass = (status) => {
    switch (status) {
      case 'Accept':
        return 'status-accept';
      case 'Reject':
        return 'status-reject';
      case 'Pending':
        return 'status-pending';
      default:
        return '';
    }
  };

  return (
    <div className="booking-history-container">
      <div className="booking-history-header">
        <div className="search-wrapper">
          <input
            type="text"
            placeholder="Search by booking ID, location, or vehicle..."
            className="history-search-input"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <span className="search-icon">🔍</span>
        </div>
      </div>

      {error && (
        <div style={{ 
          padding: '12px 16px', 
          background: '#fee2e2', 
          color: '#991b1b', 
          borderRadius: '6px', 
          marginBottom: '16px' 
        }}>
          {error}
        </div>
      )}

      {loading && bookingHistory.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
          Loading booking history...
        </div>
      ) : (
        <div className="history-table-wrapper">
          <table className="history-table">
            <thead>
              <tr>
                <th>Booking ID</th>
                <th>Booking Date</th>
                <th>From Location</th>
                <th>To Location</th>
                <th>Vehicle Number</th>
                <th>Driver Name</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedHistory.length === 0 ? (
                <tr>
                  <td colSpan="8" className="no-data">
                    {searchQuery ? 'No bookings found matching your search' : 'No booking history found'}
                  </td>
                </tr>
              ) : (
                paginatedHistory.map(booking => (
                  <tr key={booking.id || booking.numericId}>
                    <td>{booking.id}</td>
                    <td>{booking.bookingDate}</td>
                    <td>{booking.fromLocation}</td>
                    <td>{booking.toLocation}</td>
                    <td>{booking.vehicleNumber}</td>
                    <td>{booking.driverName}</td>
                    <td>
                      <span className={`status-badge ${getStatusClass(booking.status)}`}>
                        {booking.status}
                      </span>
                    </td>
                    <td>
                      <button
                        className="btn-view"
                        onClick={() => handleViewBooking(booking)}
                        title="View Details"
                        disabled={loading}
                      >
                        👁️
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="history-pagination">
          <button
            className="pagination-btn"
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1 || loading}
          >
            Previous
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
            <button
              key={page}
              className={`pagination-btn ${currentPage === page ? 'active' : ''}`}
              onClick={() => setCurrentPage(page)}
              disabled={loading}
            >
              {page}
            </button>
          ))}
          <button
            className="pagination-btn"
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages || loading}
          >
            Next
          </button>
        </div>
      )}

      {totalCount > 0 && (
        <div style={{ textAlign: 'center', marginTop: '16px', color: '#666', fontSize: '0.9rem' }}>
          Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, totalCount)} of {totalCount} bookings
        </div>
      )}

      {/* View Booking Modal */}
      {showViewModal && viewingBooking && (
        <div className="modal-overlay" onClick={() => setShowViewModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Booking Details</h2>
              <button className="modal-close" onClick={() => setShowViewModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="view-details">
                <div className="detail-row">
                  <span className="detail-label">Booking ID:</span>
                  <span className="detail-value">{viewingBooking.id}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Booking Date:</span>
                  <span className="detail-value">{viewingBooking.bookingDate}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">From Location:</span>
                  <span className="detail-value">{viewingBooking.fromLocation}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">To Location:</span>
                  <span className="detail-value">{viewingBooking.toLocation}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Vehicle Number:</span>
                  <span className="detail-value">{viewingBooking.vehicleNumber}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Driver Name:</span>
                  <span className="detail-value">{viewingBooking.driverName}</span>
                </div>
                {viewingBooking.phone && (
                  <div className="detail-row">
                    <span className="detail-label">Driver Phone:</span>
                    <span className="detail-value">{viewingBooking.phone}</span>
                  </div>
                )}
                <div className="detail-row">
                  <span className="detail-label">Status:</span>
                  <span className="detail-value">
                    <span className={`status-badge ${getStatusClass(viewingBooking.status)}`}>
                      {viewingBooking.status}
                    </span>
                  </span>
                </div>
              </div>

              {/* Product Details Section */}
              {viewingBooking.products && viewingBooking.products.length > 0 && (
                <div className="product-details-section" style={{ marginTop: '24px', paddingTop: '24px', borderTop: '1px solid #e0e0e0' }}>
                  <h3 className="section-title" style={{ fontSize: '1.1rem', fontWeight: 600, color: '#333', marginBottom: '16px' }}>Product Details</h3>
                  {viewingBooking.products.map((product, index) => (
                    <div key={index} className="product-item-detail" style={{ marginBottom: '20px', padding: '16px', background: '#f9fafb', borderRadius: '8px', border: '1px solid #e0e0e0' }}>
                      <div style={{ fontSize: '0.95rem', fontWeight: 600, color: '#1e3a8a', marginBottom: '12px' }}>
                        Product {index + 1}
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Product Name:</span>
                        <span className="detail-value">{product.productName}</span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Category:</span>
                        <span className="detail-value">
                          {product.productCategory === 'others' ? product.customCategory : product.productCategory.charAt(0).toUpperCase() + product.productCategory.slice(1)}
                        </span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Size:</span>
                        <span className="detail-value">
                          {product.length}cm × {product.height}cm × {product.width}cm
                        </span>
                      </div>
                      <div className="detail-row">
                        <span className="detail-label">Weight:</span>
                        <span className="detail-value">{product.weight} kg</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowViewModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingHistory;

